import type { ChannelAlias } from "@kupikupi/api";

/**
 * Default catalog search channel when the URL carries no explicit `?channel=`.
 * Mirrors the web catalog fix (#206/#213): 1688 is the primary sourcing
 * platform for ru-RU users, so a fresh catalog entry, a keyword search, and an
 * image search all start on 1688; the user can still switch to Taobao / Weidian
 * via the platform chips.
 *
 * Previously the mini catalog defaulted to `"taobao"` reached through an `"all"`
 * placeholder chip — the exact dead state the web fix collapsed. There is no
 * "all" channel; `parseChannel` resolves any absent / empty / legacy
 * `?channel=all` / unsupported value to {@link DEFAULT_CHANNEL}.
 */
export const DEFAULT_CHANNEL: ChannelAlias = "1688";

/** The platform chips the catalog offers. Only these are recognised as explicit
 *  channel selections; everything else (absent, legacy `all`, an unsupported
 *  channel) falls back to {@link DEFAULT_CHANNEL}. Ordered 1688 · Taobao ·
 *  Weidian to match the web sub-header (#198). */
export const SELECTABLE_CHANNELS: readonly ChannelAlias[] = ["1688", "taobao", "weidian"];

/**
 * Resolve the active catalog channel from a raw `?channel=` URL value.
 *
 * - absent / empty → {@link DEFAULT_CHANNEL} (1688).
 * - a recognised chip value (`1688` / `taobao` / `weidian`) → that channel.
 * - anything else, including the legacy `all` placeholder or an unsupported
 *   channel from an old link → {@link DEFAULT_CHANNEL}, so a stale link never
 *   lands on an empty / unselected state and the search never silently runs on
 *   Taobao.
 */
export function parseChannel(v: string | null | undefined): ChannelAlias {
  if (v && (SELECTABLE_CHANNELS as readonly string[]).includes(v)) return v as ChannelAlias;
  return DEFAULT_CHANNEL;
}
